import { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, Pencil, Trash2, Loader2, CheckCircle2, Circle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { BillFormDialog } from './BillFormDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US') : '-');

export default function BillsList() {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editBill, setEditBill] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => { fetchBills(); }, []);

  const fetchBills = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/bills`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBills(res.data || []);
    } catch (e) {
      console.error('Failed to fetch bills:', e);
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async (bill) => {
    setDownloading(bill.bill_id);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/bills/${bill.bill_id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice ${bill.reference}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(null);
    }
  };

  const togglePaid = async (bill) => {
    try {
      const token = localStorage.getItem('token');
      const newStatus = bill.status === 'paid' ? 'unpaid' : 'paid';
      await axios.put(`${API_URL}/bills/${bill.bill_id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Marked as ${newStatus}`);
      fetchBills();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const deleteBill = async (bill) => {
    if (!window.confirm(`Delete bill ${bill.reference}?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/bills/${bill.bill_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Bill deleted');
      fetchBills();
    } catch (e) {
      toast.error('Failed to delete bill');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-zinc-500" /></div>;
  }

  return (
    <div className="space-y-4">
      {bills.length === 0 ? (
        <p className="text-zinc-500 text-sm text-center py-12" data-testid="bills-empty">No bills yet. Create your first one in the Create tab.</p>
      ) : (
        <div className="rounded-md border border-zinc-800 overflow-x-auto">
          <table className="w-full text-sm" data-testid="bills-table">
            <thead>
              <tr className="border-b border-zinc-800 text-left text-zinc-500">
                <th className="px-4 py-3 font-medium">Number</th>
                <th className="px-4 py-3 font-medium">Client</th>
                <th className="px-4 py-3 font-medium">Created</th>
                <th className="px-4 py-3 font-medium">Due Date</th>
                <th className="px-4 py-3 font-medium text-right">Total</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {bills.map((bill) => (
                <tr key={bill.bill_id} className="border-b border-zinc-800/50 hover:bg-zinc-900/50" data-testid={`bill-row-${bill.number}`}>
                  <td className="px-4 py-3 font-mono text-white">{bill.reference}</td>
                  <td className="px-4 py-3 text-zinc-300">{bill.bill_to?.name}</td>
                  <td className="px-4 py-3 text-zinc-400">{fmtDate(bill.created_at)}</td>
                  <td className="px-4 py-3 text-zinc-400">{fmtDate(bill.due_date)}</td>
                  <td className="px-4 py-3 text-right text-white font-medium">${bill.total?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="px-4 py-3">
                    <Badge className={bill.status === 'paid' ? 'bg-green-500/15 text-green-400 border-green-500/30' : 'bg-amber-500/15 text-amber-400 border-amber-500/30'}>
                      {bill.status === 'paid' ? 'PAID' : 'UNPAID'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" title={bill.status === 'paid' ? 'Mark unpaid' : 'Mark paid'} onClick={() => togglePaid(bill)} data-testid={`bill-toggle-paid-${bill.number}`}>
                        {bill.status === 'paid' ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Circle className="w-4 h-4 text-zinc-500" />}
                      </Button>
                      <Button variant="ghost" size="icon" title="Download PDF" onClick={() => downloadPdf(bill)} disabled={downloading === bill.bill_id} data-testid={`bill-download-${bill.number}`}>
                        {downloading === bill.bill_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      </Button>
                      <Button variant="ghost" size="icon" title="Edit" onClick={() => { setEditBill(bill); setShowEdit(true); }} data-testid={`bill-edit-${bill.number}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" title="Delete" onClick={() => deleteBill(bill)} data-testid={`bill-delete-${bill.number}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BillFormDialog
        open={showEdit}
        onOpenChange={setShowEdit}
        bill={editBill}
        onSaved={fetchBills}
      />
    </div>
  );
}
